/* femme Appstle cart conversion V14 | 2026-11-03
   Keeps the signed-off cart geometry and cadence chooser behaviour unchanged.
   V13 adds Supply_XX-driven recommended cadence guidance to the cart plan modal.
   Recommendation is advisory only and never auto-selects a selling plan.
   V14 suppresses cart subscription conversion while a line has the active
   Near Expiry / short-date automatic discount, and guards the mutation path so
   stale controls cannot convert discounted short-date stock into a subscription.
*/
(() => {
  'use strict';

  const A = window.FemmeAppstleMigration;
  if (!A) return;

  const SUPPORTED_CADENCES = [
    'Every 2 weeks',
    'Every month',
    'Every 2 months',
    'Every 3 months'
  ];
  const CADENCE_ORDER = new Map(
    SUPPORTED_CADENCES.map((label, index) => [label, index])
  );

  const displayPlanName = (planOrName) => {
    const plan =
      typeof planOrName === 'string'
        ? { name: planOrName }
        : planOrName || {};
    const name = String(plan.name || '').trim();

    if (/fortnight|every\s*2\s*weeks?/i.test(name)) return 'Every 2 weeks';
    if (/\b3\s*monthly\b|every\s*3\s*months?/i.test(name)) return 'Every 3 months';
    if (/\b2\s*monthly\b|every\s*2\s*months?/i.test(name)) return 'Every 2 months';
    if (/\b1\s*monthly\b|every\s*month\b|monthly\s*subscription/i.test(name)) return 'Every month';

    return (
      (typeof A.planDisplayLabel === 'function'
        ? A.planDisplayLabel(plan)
        : name) ||
      'Subscription'
    );
  };

  const normalisePlanId = (value) =>
    typeof A.normaliseId === 'function'
      ? A.normaliseId(value)
      : String(value || '').replace(/^gid:\/\/shopify\/[^/]+\//, '');

  const fetchCart = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`Unable to read cart (${response.status}).`);
    }

    return response.json();
  };

  const fetchConfig = () =>
    typeof window.FoxTheme?.utils?.fetchConfig === 'function'
      ? window.FoxTheme.utils.fetchConfig()
      : {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        };

  const getComponentInput = (component) =>
    component?.querySelector('.femme-cart-subscription__input') ||
    component?.querySelector('input[data-subscription-toggle]') ||
    null;

  const getItem = (element) => element?.closest('.cart-item') || null;

  const getLineKey = (component, item) =>
    String(component?.dataset?.lineKey || item?.dataset?.cartLineKey || '');

  const getLineIndex = (component) => {
    const value = Number(component?.dataset?.lineIndex || 0);
    return Number.isInteger(value) && value > 0 ? value : 0;
  };

  const getVariantId = (item) => String(item?.dataset?.variantId || '');

  const getMeta = (component, item) =>
    component?.querySelector('.femme-cart-subscription__meta') ||
    component?.querySelector('.femme-cart-subscription__text small') ||
    item?.querySelector('.femme-cart-subscription__meta') ||
    item?.querySelector('.femme-cart-subscription__text small') ||
    null;

  const getStatus = (component) =>
    component?.querySelector('.femme-cart-subscription__status') || null;

  const cartItemForComponent = (cart, component, item) => {
    const cartItems = Array.isArray(cart?.items) ? cart.items : [];
    const lineKey = getLineKey(component, item);

    if (lineKey) {
      const exact = cartItems.find(
        (cartItem) => String(cartItem?.key || '') === lineKey
      );
      if (exact) return exact;
    }

    const line = getLineIndex(component);
    return line > 0 ? cartItems[line - 1] || null : null;
  };

  const isNearExpiryDiscountTitle = (value) =>
    /short[\s-]*date\s+saving|near[\s-]*expir/i.test(String(value || ''));

  const cartItemHasNearExpiryDiscount = (cartItem) => {
    const discounts = Array.isArray(cartItem?.discounts)
      ? cartItem.discounts
      : [];
    const allocations = Array.isArray(cartItem?.line_level_discount_allocations)
      ? cartItem.line_level_discount_allocations
      : [];

    return (
      discounts.some((discount) => isNearExpiryDiscountTitle(discount?.title)) ||
      allocations.some((allocation) =>
        isNearExpiryDiscountTitle(
          allocation?.discount_application?.title || allocation?.title
        )
      )
    );
  };

  const itemHasNearExpiryDiscountText = (item) =>
    Array.from(
      item?.querySelectorAll?.('.cart-item__discounts, .discount') || []
    ).some((node) => isNearExpiryDiscountTitle(node.textContent));

  const isKnownNearExpiryLine = (component, item, cartItem = null) =>
    component?.dataset?.femmeNearExpiry === 'true' ||
    cartItemHasNearExpiryDiscount(cartItem) ||
    itemHasNearExpiryDiscountText(item);

  const lockNearExpiryComponent = (component) => {
    if (!component) return;

    component.dataset.femmeNearExpiry = 'true';
    component.dataset.cadencePending = 'false';
    component.hidden = true;

    const input = getComponentInput(component);
    if (input) {
      input.checked = false;
      input.disabled = true;
      input.setAttribute('aria-disabled', 'true');
    }
  };

  const unlockNearExpiryComponent = (component) => {
    if (!component || component.dataset.femmeNearExpiry !== 'true') return;

    delete component.dataset.femmeNearExpiry;

    const input = getComponentInput(component);
    if (input) {
      const requiresSellingPlan =
        String(component.dataset.requiresSellingPlan || '') === 'true';
      input.disabled = requiresSellingPlan;
      if (!requiresSellingPlan) input.removeAttribute('aria-disabled');
    }
  };

  const currentPlan = (cartItem) =>
    cartItem?.selling_plan_allocation?.selling_plan || null;

  const currentPlanId = (cartItem) => normalisePlanId(currentPlan(cartItem)?.id);

  const supplyDays = (product) => {
    const rawTags = Array.isArray(product?.tags)
      ? product.tags
      : typeof product?.tags === 'string'
        ? product.tags.split(',')
        : [];

    const supplyTags = rawTags
      .map((tag) => String(tag || '').trim())
      .filter((tag) => /^supply_/i.test(tag));

    if (supplyTags.length !== 1) return null;

    const match = supplyTags[0].match(/^supply_(\d+(?:\.\d+)?)$/i);
    if (!match) return null;

    const days = Number(match[1]);
    return Number.isFinite(days) && days > 0 ? days : null;
  };

  const cadenceDays = (plan) => {
    const label = displayPlanName(plan);
    if (label === 'Every 2 weeks') return 14;
    if (label === 'Every month') return 30;
    if (label === 'Every 2 months') return 60;
    if (label === 'Every 3 months') return 90;
    return null;
  };

  const recommendedPlanId = (product, plans) => {
    const days = supplyDays(product);
    if (!days || !Array.isArray(plans) || !plans.length) return '';

    let bestId = '';
    let bestDistance = Infinity;
    let bestCadence = Infinity;

    plans.forEach((plan) => {
      const planDays = cadenceDays(plan);
      if (!planDays) return;

      const distance = Math.abs(days - planDays);
      if (
        distance < bestDistance ||
        (distance === bestDistance && planDays < bestCadence)
      ) {
        bestId = normalisePlanId(plan.id);
        bestDistance = distance;
        bestCadence = planDays;
      }
    });

    return bestId;
  };

  const availablePlans = async (item) => {
    const handle = String(item?.dataset?.handle || '');
    const variantId = getVariantId(item);
    if (!handle || !variantId) return [];

    const product = await A.fetchProduct(handle);
    const rawPlans = A.appstleData(product).get(variantId)?.plans || [];

    const plans = rawPlans
      .map((plan) => ({
        ...plan,
        id: normalisePlanId(plan.id),
        displayLabel: displayPlanName(plan)
      }))
      .filter(
        (plan) =>
          plan.id &&
          SUPPORTED_CADENCES.includes(plan.displayLabel)
      )
      .sort(
        (a, b) =>
          (CADENCE_ORDER.get(a.displayLabel) ?? 999) -
          (CADENCE_ORDER.get(b.displayLabel) ?? 999)
      );

    const recommendation = recommendedPlanId(product, plans);

    return plans.map((plan) => ({
      ...plan,
      isRecommended:
        Boolean(recommendation) &&
        normalisePlanId(plan.id) === recommendation
    }));
  };

  const installStyles = () => {
    const existing = document.getElementById('femme-appstle-cart-cadence-styles');
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = 'femme-appstle-cart-cadence-styles';
    style.textContent = `
      body.femme-subscription-modal-open {
        overflow: hidden;
      }

      .femme-subscription-modal:not([open]) {
        display: none !important;
      }

      .femme-subscription-modal {
        position: fixed;
        inset: 0;
        width: 100vw;
        height: 100dvh;
        max-width: none;
        max-height: none;
        margin: 0;
        padding: 0;
        border: 0;
        background: transparent;
        color: inherit;
        overflow: hidden;
      }

      .femme-subscription-modal[open] {
        display: block;
      }

      .femme-subscription-modal::backdrop {
        background: rgba(0, 15, 32, 0.48);
        backdrop-filter: blur(2px);
        -webkit-backdrop-filter: blur(2px);
      }

      .femme-subscription-modal__dialog {
        position: fixed;
        left: 12px;
        top: 12px;
        width: min(350px, calc(100vw - 24px));
        padding: 18px;
        border-radius: 18px;
        background: #ffffff;
        box-shadow: 0 24px 70px rgba(0, 15, 32, 0.24);
        color: #000f20;
        transform: translateY(7px) scale(0.985);
        transform-origin: bottom center;
        transition: transform 190ms cubic-bezier(0.4, 0, 0.2, 1), opacity 150ms ease;
        opacity: 0;
      }

      .femme-subscription-modal.is-open .femme-subscription-modal__dialog {
        transform: translateY(0) scale(1);
        opacity: 1;
      }

      .femme-subscription-modal__header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 14px;
        margin-bottom: 14px;
      }

      .femme-subscription-modal__heading {
        margin: 0;
        color: #000f20;
        font-size: 17px;
        font-weight: 700;
        line-height: 1.2;
      }

      .femme-subscription-modal__intro {
        margin: 4px 0 0;
        color: rgba(0, 15, 32, 0.68);
        font-size: 12px;
        line-height: 1.35;
      }

      .femme-subscription-modal__close,
      .femme-subscription-modal__option,
      .femme-cart-subscription__change,
      .femme-cart-subscription__choose {
        appearance: none;
        -webkit-appearance: none;
        border: 0;
        font: inherit;
        cursor: pointer;
      }

      .femme-subscription-modal__close {
        position: relative;
        width: 30px;
        height: 30px;
        flex: 0 0 30px;
        border-radius: 999px;
        background: #f7f4e1;
        color: #000f20;
        transition: background-color 130ms ease, transform 130ms ease;
      }

      .femme-subscription-modal__close::before,
      .femme-subscription-modal__close::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 12px;
        height: 1.5px;
        border-radius: 2px;
        background: currentColor;
      }

      .femme-subscription-modal__close::before {
        transform: translate(-50%, -50%) rotate(45deg);
      }

      .femme-subscription-modal__close::after {
        transform: translate(-50%, -50%) rotate(-45deg);
      }

      .femme-subscription-modal__close:hover,
      .femme-subscription-modal__close:focus-visible {
        outline: none;
        background: #cfcd70;
      }

      .femme-subscription-modal__close:focus-visible,
      .femme-subscription-modal__option:focus-visible,
      .femme-cart-subscription__change:focus-visible,
      .femme-cart-subscription__choose:focus-visible {
        box-shadow: 0 0 0 2px rgba(124, 41, 70, 0.22);
        outline: none;
      }

      .femme-subscription-modal__close:active {
        transform: scale(0.96);
      }

      .femme-subscription-modal__options {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 7px;
      }

      .femme-subscription-modal__option {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        min-height: 50px;
        padding: 7px 10px;
        border-radius: 999px;
        background: #f7f4e1;
        color: #000f20;
        font-size: 12px;
        font-weight: 650;
        line-height: 1.1;
        text-align: center;
        transition: background-color 130ms ease, transform 130ms ease;
      }

      .femme-subscription-modal__option-label {
        display: block;
        line-height: 1.1;
      }

      .femme-subscription-modal__recommended {
        display: block;
        color: #000f20;
        font-size: 9px;
        font-weight: 500;
        line-height: 1;
        white-space: nowrap;
      }

      .femme-subscription-modal__option:hover,
      .femme-subscription-modal__option:focus-visible {
        outline: none;
        background: rgba(207, 205, 112, 0.68);
      }

      .femme-subscription-modal__option:active {
        transform: scale(0.985);
      }

      .femme-subscription-modal__option[aria-selected='true'] {
        background: #cfcd70;
        color: #000000;
        font-weight: 700;
      }

      .femme-subscription-modal__status {
        min-height: 0;
        margin: 10px 0 0;
        color: #7c2946;
        font-size: 11px;
        line-height: 1.3;
        text-align: center;
      }

      .femme-subscription-modal.is-loading .femme-subscription-modal__dialog {
        opacity: 0.72;
      }

      .femme-subscription-modal.is-loading button {
        pointer-events: none;
      }

      .femme-cart-drawer .cart-item__details,
      .femme-cart-drawer .cart-item__action {
        min-height: 92px;
      }

      .femme-cart-drawer .cart-quantity {
        margin-top: auto;
      }

      .femme-cart-subscription[data-femme-near-expiry='true'] {
        display: none !important;
      }

      .femme-cart-subscription {
        box-sizing: border-box;
        height: 32px;
        min-height: 32px;
        margin-top: auto !important;
        padding-top: 0 !important;
      }

      .femme-cart-subscription__label {
        box-sizing: border-box;
        min-height: 32px !important;
        height: 32px;
        align-items: stretch !important;
        gap: 10px;
      }

      .femme-cart-subscription__switch {
        height: 32px;
        align-items: flex-start !important;
        margin-top: 0 !important;
        transform: none !important;
      }

      .femme-cart-subscription__text {
        display: flex !important;
        flex-direction: column;
        justify-content: space-between;
        align-self: stretch;
        height: 32px;
        min-height: 32px;
        gap: 0 !important;
        line-height: 1;
      }

      .femme-cart-subscription__text strong {
        display: block;
        line-height: 1 !important;
      }

      .femme-cart-subscription__text small {
        display: block;
        min-height: 0;
        margin: 0 !important;
        line-height: 1 !important;
        transform: translateY(-6px);
      }

      .femme-cart-subscription__change,
      .femme-cart-subscription__choose {
        display: inline;
        margin: 0;
        padding: 0;
        background: transparent;
        color: inherit;
        line-height: inherit;
        text-decoration: underline;
        text-underline-offset: 2px;
      }

      .femme-cart-subscription__change:hover,
      .femme-cart-subscription__choose:hover {
        text-decoration-thickness: 2px;
      }

      @media (max-width: 380px) {
        .femme-subscription-modal__dialog {
          width: calc(100vw - 20px);
          padding: 16px;
          border-radius: 16px;
        }

        .femme-subscription-modal__options {
          grid-template-columns: 1fr;
          gap: 6px;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .femme-subscription-modal__dialog,
        .femme-subscription-modal__close,
        .femme-subscription-modal__option {
          transition: none !important;
        }
      }
    `;

    document.head.appendChild(style);
  };

  const getModal = () =>
    document.querySelector(
      '.femme-subscription-modal[data-femme-appstle-cart-modal]'
    );

  const clearModalLoading = (modal) => {
    if (!modal) return;
    modal.classList.remove('is-loading');
    modal.querySelectorAll('button').forEach((button) => {
      button.disabled = false;
    });
  };

  const createModal = () => {
    let modal = getModal();
    if (modal) return modal;

    modal = document.createElement('dialog');
    modal.className = 'femme-subscription-modal';
    modal.dataset.femmeAppstleCartModal = 'true';
    modal.setAttribute('aria-labelledby', 'femme-subscription-modal-title');
    modal.setAttribute('aria-describedby', 'femme-subscription-modal-intro');

    const dialog = document.createElement('section');
    dialog.className = 'femme-subscription-modal__dialog';

    const header = document.createElement('div');
    header.className = 'femme-subscription-modal__header';

    const copy = document.createElement('div');

    const heading = document.createElement('h2');
    heading.id = 'femme-subscription-modal-title';
    heading.className = 'femme-subscription-modal__heading';
    heading.textContent = 'Choose your delivery frequency';

    const intro = document.createElement('p');
    intro.id = 'femme-subscription-modal-intro';
    intro.className = 'femme-subscription-modal__intro';
    intro.textContent = 'How often would you like us to send it?';

    copy.append(heading, intro);

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'femme-subscription-modal__close';
    close.dataset.femmeCadenceDismiss = 'true';
    close.setAttribute('aria-label', 'Close subscription frequency selector');

    header.append(copy, close);

    const options = document.createElement('div');
    options.className = 'femme-subscription-modal__options';
    options.dataset.femmeCadenceOptions = 'true';
    options.setAttribute('role', 'listbox');
    options.setAttribute('aria-label', 'Subscription delivery frequency');

    const status = document.createElement('p');
    status.className = 'femme-subscription-modal__status';
    status.dataset.femmeCadenceModalStatus = 'true';
    status.setAttribute('aria-live', 'polite');

    dialog.append(header, options, status);
    modal.appendChild(dialog);
    document.body.appendChild(modal);

    modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (modal.classList.contains('is-loading')) return;
      closeModal(modal, { cancelPending: true, restoreFocus: true });
    });

    return modal;
  };

  const renderModalOptions = (modal, plans, selectedPlanId = '') => {
    const options = modal.querySelector('[data-femme-cadence-options]');
    if (!options) return;

    options.replaceChildren();

    plans.forEach((plan) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'femme-subscription-modal__option';
      option.dataset.femmeCadenceOption = 'true';
      option.dataset.planId = normalisePlanId(plan.id);
      option.dataset.planName = String(plan.name || '');
      if (plan.isRecommended) option.dataset.femmeRecommended = 'true';
      option.setAttribute('role', 'option');

      const isSelected =
        normalisePlanId(plan.id) === normalisePlanId(selectedPlanId);
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');

      const label = document.createElement('span');
      label.className = 'femme-subscription-modal__option-label';
      label.textContent = plan.displayLabel;
      option.appendChild(label);

      if (plan.isRecommended) {
        const recommended = document.createElement('span');
        recommended.className = 'femme-subscription-modal__recommended';
        recommended.textContent = '★ Recommended ★';
        option.appendChild(recommended);
        option.setAttribute(
          'aria-label',
          `${plan.displayLabel}, recommended for this product`
        );
      } else {
        option.setAttribute('aria-label', plan.displayLabel);
      }

      options.appendChild(option);
    });
  };

  const renderMeta = (meta, label = '') => {
    if (!meta) return;

    meta.hidden = false;
    meta.replaceChildren();

    if (!label) {
      const choose = document.createElement('button');
      choose.type = 'button';
      choose.className = 'femme-cart-subscription__choose';
      choose.dataset.femmeCadenceChoose = 'true';
      choose.textContent = 'Choose your plan';
      choose.setAttribute('aria-label', 'Choose a subscription delivery frequency');
      meta.appendChild(choose);
      return;
    }

    meta.appendChild(document.createTextNode(`${label} - `));

    const change = document.createElement('button');
    change.type = 'button';
    change.className = 'femme-cart-subscription__change';
    change.dataset.femmeCadenceChange = 'true';
    change.textContent = 'change';
    change.setAttribute(
      'aria-label',
      `Change subscription delivery frequency from ${label}`
    );

    meta.appendChild(change);
  };

  const positionModal = (modal, component) => {
    if (!modal?.open) return;

    const panel = modal.querySelector('.femme-subscription-modal__dialog');
    const anchor =
      component?.querySelector('.femme-cart-subscription__track') || component;
    if (!panel || !anchor?.isConnected) return;

    const anchorRect = anchor.getBoundingClientRect();
    const panelWidth = panel.offsetWidth;
    const panelHeight = panel.offsetHeight;
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    const edge = 12;
    const gap = 12;

    let left = anchorRect.left + anchorRect.width / 2 - panelWidth / 2;
    left = Math.max(edge, Math.min(left, viewportWidth - panelWidth - edge));

    let top = anchorRect.top - panelHeight - gap;
    if (top < edge) {
      top = anchorRect.bottom + gap;
    }
    top = Math.max(edge, Math.min(top, viewportHeight - panelHeight - edge));

    panel.style.left = `${Math.round(left)}px`;
    panel.style.top = `${Math.round(top)}px`;
  };

  const positionOpenModal = () => {
    const modal = getModal();
    if (!modal?.open) return;

    const component = modal._femmeComponent;
    if (!component?.isConnected) {
      closeModal(modal, { cancelPending: true });
      return;
    }

    positionModal(modal, component);
  };

  const openModal = (modal, component, item, plans, selectedPlanId = '') => {
    if (!modal) return;

    if (modal._femmeCloseTimer) {
      window.clearTimeout(modal._femmeCloseTimer);
      modal._femmeCloseTimer = null;
    }

    clearModalLoading(modal);
    modal.classList.remove('is-open');
    modal._femmeComponent = component;
    modal._femmeItem = item;
    modal._femmePlans = plans;

    const modalStatus = modal.querySelector('[data-femme-cadence-modal-status]');
    if (modalStatus) modalStatus.textContent = '';

    renderModalOptions(modal, plans, selectedPlanId);
    document.body.classList.add('femme-subscription-modal-open');

    if (!modal.open) {
      modal.showModal();
    }

    positionModal(modal, component);

    window.requestAnimationFrame(() => {
      positionModal(modal, component);
      modal.classList.add('is-open');
      const selected = modal.querySelector(
        '[data-femme-cadence-option][aria-selected="true"]'
      );
      (selected || modal.querySelector('[data-femme-cadence-option]'))?.focus({
        preventScroll: true
      });
    });
  };

  const closeModal = (
    modal,
    { cancelPending = false, restoreFocus = false } = {}
  ) => {
    if (!modal || !modal.open) return;

    const component = modal._femmeComponent;
    const input = getComponentInput(component);

    if (
      cancelPending &&
      component?.dataset.cadencePending === 'true' &&
      !component.dataset.currentPlanId
    ) {
      component.dataset.cadencePending = 'false';
      if (input) input.checked = false;
    }

    clearModalLoading(modal);
    modal.classList.remove('is-open');
    document.body.classList.remove('femme-subscription-modal-open');

    const finish = () => {
      if (modal.open) modal.close();
      modal._femmeComponent = null;
      modal._femmeItem = null;
      modal._femmePlans = null;
      modal._femmeCloseTimer = null;

      if (restoreFocus && input?.isConnected) {
        input.focus({ preventScroll: true });
      }
    };

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      finish();
      return;
    }

    modal._femmeCloseTimer = window.setTimeout(finish, 180);
  };

  const setLoading = (component, modal, loading) => {
    component?.classList.toggle('is-loading', loading);
    modal?.classList.toggle('is-loading', loading);

    if (loading) {
      component?.setAttribute('aria-busy', 'true');
    } else {
      component?.removeAttribute('aria-busy');
    }

    const input = getComponentInput(component);
    if (input) input.disabled = loading;

    modal?.querySelectorAll('button').forEach((button) => {
      button.disabled = loading;
    });
  };

  const cancelPending = (component, { restoreFocus = false } = {}) => {
    if (!component || component.dataset.cadencePending !== 'true') return;

    const input = getComponentInput(component);
    const modal = getModal();

    component.dataset.cadencePending = 'false';
    if (input) input.checked = false;
    closeModal(modal, { cancelPending: false, restoreFocus });
  };

  const groupedSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {
        bubbles: true,
        detail: { sections }
      })
    );
    return sections;
  };

  const verifyMutation = (cart, component, item, expectedPlanId) => {
    const cartItems = Array.isArray(cart?.items) ? cart.items : [];
    const line = getLineIndex(component);
    const variantId = getVariantId(item);

    let target = line > 0 ? cartItems[line - 1] || null : null;
    if (target && variantId && String(target.variant_id || '') !== variantId) {
      target = null;
    }

    if (!target) {
      const sameVariant = cartItems.filter(
        (cartItem) => String(cartItem?.variant_id || '') === variantId
      );
      if (sameVariant.length === 1) target = sameVariant[0];
    }

    if (!target) {
      throw new Error('Unable to verify the updated cart line.');
    }

    const actualPlanId = currentPlanId(target);
    const expected = normalisePlanId(expectedPlanId);

    if (expected && actualPlanId !== expected) {
      throw new Error('Shopify did not apply the selected subscription cadence.');
    }

    if (!expected && actualPlanId) {
      throw new Error('Shopify did not remove the selling plan from this line.');
    }

    return target;
  };

  const publishCartUpdate = (cart, input, component) => {
    const line = getLineIndex(component);

    if (
      window.FoxTheme?.pubsub?.publish &&
      window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate
    ) {
      window.FoxTheme.pubsub.publish(
        window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        {
          sources: 'femme-appstle-cart-subscription',
          cart,
          target: input,
          line,
          name: 'selling_plan'
        }
      );
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { source: 'femme-appstle-cart-subscription', cart }
      })
    );
  };

  const changeSellingPlan = async (component, item, plan) => {
    const input = getComponentInput(component);
    const modal = getModal();
    const status = getStatus(component);
    const modalStatus = modal?.querySelector('[data-femme-cadence-modal-status]');
    const lineKey = getLineKey(component, item);
    const planId = plan?.id ? normalisePlanId(plan.id) : '';

    if (!input || !lineKey) {
      throw new Error('Cart line is missing its subscription controls.');
    }

    if (plan) {
      let nearExpiryActive = isKnownNearExpiryLine(component, item);

      if (!nearExpiryActive) {
        try {
          const liveCart = await fetchCart();
          nearExpiryActive = cartItemHasNearExpiryDiscount(
            cartItemForComponent(liveCart, component, item)
          );
        } catch (error) {
          console.error('[femme Appstle] Near Expiry cart guard check failed.', error);
        }
      }

      if (nearExpiryActive) {
        lockNearExpiryComponent(component);
        closeModal(modal, { cancelPending: false });
        throw new Error('Subscription is unavailable for this short-date batch.');
      }
    }

    setLoading(component, modal, true);
    if (status) status.textContent = '';
    if (modalStatus) modalStatus.textContent = '';

    try {
      const sections = groupedSections();
      const url =
        window.FoxTheme?.routes?.cart_change_url ||
        `${window.Shopify?.routes?.root || '/'}cart/change.js`;

      const response = await fetch(url, {
        ...fetchConfig(),
        credentials: 'same-origin',
        body: JSON.stringify({
          id: lineKey,
          selling_plan: planId ? Number(planId) : null,
          sections
        })
      });

      const cart = await response.json();
      if (!response.ok || cart?.errors) {
        throw new Error(
          cart?.description ||
            cart?.message ||
            cart?.errors ||
            'Unable to update subscription'
        );
      }

      const updatedItem = verifyMutation(cart, component, item, planId);
      const appliedPlan = currentPlan(updatedItem);
      const appliedLabel = appliedPlan ? displayPlanName(appliedPlan) : '';
      const meta = getMeta(component, item);

      component.dataset.cadencePending = 'false';
      component.dataset.currentPlanId = planId;
      input.checked = Boolean(planId);

      if (planId) {
        input.dataset.sellingPlanName = String(appliedPlan?.name || plan?.name || '');
        input.dataset.femmeSellingPlanName = appliedLabel;
        renderMeta(meta, appliedLabel);
        closeModal(modal, { cancelPending: false });
      } else {
        delete input.dataset.sellingPlanName;
        delete input.dataset.femmeSellingPlanName;
        renderMeta(meta, '');
      }

      publishCartUpdate(cart, input, component);
      scheduleSync();
      return cart;
    } finally {
      /* Always clear the shared modal state, even if the cart redraw disconnected
         the original component before this request finished. */
      setLoading(component, modal, false);
      clearModalLoading(modal);
    }
  };

  const syncComponent = async (component, cart) => {
    const item = getItem(component);
    const input = getComponentInput(component);
    if (!item || !(input instanceof HTMLInputElement)) return;

    const cartItem = cartItemForComponent(cart, component, item);
    if (!cartItem) return;

    const nearExpiryActive =
      cartItemHasNearExpiryDiscount(cartItem) || itemHasNearExpiryDiscountText(item);

    if (nearExpiryActive) {
      lockNearExpiryComponent(component);
      const modal = getModal();
      if (modal?._femmeComponent === component && modal.open) {
        closeModal(modal, { cancelPending: false });
      }
      return;
    }

    unlockNearExpiryComponent(component);

    let plans = [];
    try {
      plans = await availablePlans(item);
    } catch (error) {
      console.error('[femme Appstle] Unable to load cart cadence plans.', error);
    }

    const activePlan = currentPlan(cartItem);
    const activePlanId = currentPlanId(cartItem);
    const activeLabel = activePlan ? displayPlanName(activePlan) : '';
    const meta = getMeta(component, item);
    const pending = component.dataset.cadencePending === 'true';

    component.dataset.currentPlanId = activePlanId;
    input.checked = Boolean(activePlan) || pending;

    if (activePlan) {
      input.dataset.sellingPlanName = String(activePlan.name || '');
      input.dataset.femmeSellingPlanName = activeLabel;
      renderMeta(meta, activeLabel);
    } else {
      delete input.dataset.sellingPlanName;
      delete input.dataset.femmeSellingPlanName;
      renderMeta(meta, '');
    }

    if (!plans.length) {
      if (!activePlan) component.hidden = true;
      return;
    }

    component.hidden = false;
  };

  A.syncCartCadenceLabels = async () => {
    installStyles();
    createModal();

    const components = Array.from(
      document.querySelectorAll('femme-cart-subscription')
    );
    if (!components.length) return;

    components.forEach((component) => {
      const item = getItem(component);
      if (itemHasNearExpiryDiscountText(item)) {
        lockNearExpiryComponent(component);
      }
    });

    let cart = null;
    try {
      cart = await fetchCart();
    } catch (error) {
      console.error('[femme Appstle] Cart cadence read failed.', error);
      return;
    }

    await Promise.all(
      components.map((component) => syncComponent(component, cart))
    );
  };

  const scheduleSync = () => {
    [0, 120, 320].forEach((delay) => {
      window.setTimeout(() => {
        Promise.resolve(A.syncCartCadenceLabels()).catch((error) => {
          console.error('[femme Appstle] Cart cadence sync failed.', error);
        });
      }, delay);
    });
  };

  const showChooser = async (component, item, { pending = true } = {}) => {
    const input = getComponentInput(component);
    const status = getStatus(component);
    if (!input) return;

    if (isKnownNearExpiryLine(component, item)) {
      lockNearExpiryComponent(component);
      return;
    }

    let plans = [];
    try {
      plans = await availablePlans(item);
    } catch (error) {
      console.error('[femme Appstle] Cart cadence lookup failed.', error);
    }

    if (!plans.length) {
      if (pending) input.checked = false;
      component.dataset.cadencePending = 'false';
      if (status) status.textContent = 'Subscription unavailable';
      return;
    }

    const modal = createModal();
    clearModalLoading(modal);
    component.dataset.cadencePending = pending ? 'true' : 'false';
    if (pending) input.checked = true;

    openModal(
      modal,
      component,
      item,
      plans,
      pending ? '' : normalisePlanId(component.dataset.currentPlanId)
    );
  };

  const handleToggle = async (event) => {
    const input = event.target;
    if (
      !(input instanceof HTMLInputElement) ||
      !input.matches('.femme-cart-subscription__input') ||
      input.disabled
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const component = input.closest('femme-cart-subscription');
    const item = getItem(input);
    if (!component || !item) return;

    if (isKnownNearExpiryLine(component, item)) {
      lockNearExpiryComponent(component);
      return;
    }

    const status = getStatus(component);
    if (status) status.textContent = '';

    const wasActive = Boolean(component.dataset.currentPlanId);
    const isPending = component.dataset.cadencePending === 'true';

    if (!input.checked) {
      if (isPending && !wasActive) {
        cancelPending(component);
        return;
      }

      if (!wasActive) {
        input.checked = false;
        return;
      }

      try {
        await changeSellingPlan(component, item, null);
      } catch (error) {
        console.error('[femme Appstle] Cart conversion failed.', error);
        input.checked = true;
        if (status) status.textContent = 'Try again';
      }
      return;
    }

    if (wasActive) {
      input.checked = true;
      return;
    }

    await showChooser(component, item, { pending: true });
  };

  const handleClick = async (event) => {
    const option = event.target.closest?.('[data-femme-cadence-option]');
    if (option) {
      const modal = option.closest('.femme-subscription-modal');
      const component = modal?._femmeComponent;
      const item = modal?._femmeItem;
      const input = getComponentInput(component);
      if (!modal || !component || !item || !input || option.disabled) return;

      event.preventDefault();
      event.stopPropagation();

      if (isKnownNearExpiryLine(component, item)) {
        lockNearExpiryComponent(component);
        closeModal(modal, { cancelPending: false });
        return;
      }

      const planId = normalisePlanId(option.dataset.planId);
      const plans = Array.isArray(modal._femmePlans) ? modal._femmePlans : [];
      const plan = plans.find(
        (candidate) => normalisePlanId(candidate.id) === planId
      );
      const status = getStatus(component);
      const modalStatus = modal.querySelector('[data-femme-cadence-modal-status]');

      if (!plan) {
        if (modalStatus) modalStatus.textContent = 'That option is unavailable. Please try again.';
        if (status) status.textContent = 'Subscription unavailable';
        return;
      }

      if (
        component.dataset.cadencePending !== 'true' &&
        normalisePlanId(component.dataset.currentPlanId) === planId
      ) {
        closeModal(modal, { cancelPending: false, restoreFocus: true });
        return;
      }

      try {
        await changeSellingPlan(component, item, plan);
      } catch (error) {
        console.error('[femme Appstle] Cart cadence change failed.', error);
        if (modalStatus) modalStatus.textContent = 'Something went wrong. Please try again.';
        if (status) status.textContent = 'Try again';
      }
      return;
    }

    const choose = event.target.closest?.('[data-femme-cadence-choose]');
    if (choose) {
      const component = choose.closest('femme-cart-subscription');
      const item = getItem(choose);
      const input = getComponentInput(component);
      if (!component || !item || !input || input.disabled) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (component.dataset.currentPlanId) {
        await showChooser(component, item, { pending: false });
      } else {
        await showChooser(component, item, { pending: true });
      }
      return;
    }

    const change = event.target.closest?.('[data-femme-cadence-change]');
    if (change) {
      const component = change.closest('femme-cart-subscription');
      const item = getItem(change);
      if (!component || !item || !component.dataset.currentPlanId) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      await showChooser(component, item, { pending: false });
      return;
    }

    const dismiss = event.target.closest?.('[data-femme-cadence-dismiss]');
    if (dismiss) {
      const modal = dismiss.closest('.femme-subscription-modal');
      if (!modal || modal.classList.contains('is-loading')) return;

      event.preventDefault();
      event.stopPropagation();
      closeModal(modal, { cancelPending: true, restoreFocus: true });
      return;
    }

    const modal = getModal();
    if (
      modal?.open &&
      event.target === modal &&
      !modal.classList.contains('is-loading')
    ) {
      event.preventDefault();
      event.stopPropagation();
      closeModal(modal, { cancelPending: true, restoreFocus: true });
    }
  };

  const handleKeydown = (event) => {
    if (event.key !== 'Escape') return;

    const modal = getModal();
    if (!modal?.open) return;

    /* Consume Escape before the theme drawer sees it. */
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (modal.classList.contains('is-loading')) return;
    closeModal(modal, { cancelPending: true, restoreFocus: true });
  };

  document.addEventListener('change', handleToggle, true);
  document.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKeydown, true);
  document.addEventListener('scroll', positionOpenModal, true);
  window.addEventListener('resize', positionOpenModal);
  document.addEventListener('cart:updated', scheduleSync);
  document.addEventListener('cart:refresh', scheduleSync);
  document.addEventListener('product-ajax:added', scheduleSync);
  document.addEventListener('ajaxProduct:added', scheduleSync);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
  } else {
    scheduleSync();
  }
})();